import { useState } from "react";
import Layout from "../components/Layout";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Home,
  TrendingUp,
  DollarSign,
  Search,
  Loader2,
  MapPin,
  Building,
  Calculator,
  FileText
} from "lucide-react";

export default function RealEstateResearch() {
  const { api } = useAuth();
  
  // House Flip State
  const [flipAddress, setFlipAddress] = useState("");
  const [flipPrice, setFlipPrice] = useState("");
  const [flipAnalysis, setFlipAnalysis] = useState(null);
  const [flipLoading, setFlipLoading] = useState(false);
  
  // Rent Value State
  const [rentAddress, setRentAddress] = useState("");
  const [rentBedrooms, setRentBedrooms] = useState("");
  const [rentBathrooms, setRentBathrooms] = useState("");
  const [rentSqft, setRentSqft] = useState("");
  const [rentAnalysis, setRentAnalysis] = useState(null);
  const [rentLoading, setRentLoading] = useState(false);

  const analyzeFlip = async () => {
    if (!flipAddress.trim()) {
      toast.error("Please enter an address");
      return;
    }
    
    setFlipLoading(true);
    setFlipAnalysis(null);
    toast.info("🔍 Searching property listings & analyzing...", { duration: 5000 });
    
    try {
      const res = await api.post("/ai/real-estate/house-flip-analysis", {
        address: flipAddress,
        purchase_price: flipPrice ? parseFloat(flipPrice) : null
      });
      setFlipAnalysis(res.data);
      if (res.data.web_research?.is_active_listing) {
        toast.success(`✅ ACTIVE MLS Listing found! Sources: ${res.data.web_research.sources_searched?.join(', ')}`);
      } else if (res.data.web_research?.listing_status === 'sold') {
        toast.warning("❌ Property is SOLD - Not available for purchase");
      } else if (res.data.web_research?.listing_status === 'pending') {
        toast.warning("⏳ Property is PENDING - Under contract");
      } else if (res.data.web_research?.listing_status === 'off_market') {
        toast.warning("🚫 Property is OFF-MARKET - Not currently listed");
      } else {
        toast.info("❓ No active listing found - Using market estimates");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Analysis failed");
    } finally {
      setFlipLoading(false);
    }
  };

  const analyzeRent = async () => {
    if (!rentAddress.trim()) {
      toast.error("Please enter an address");
      return;
    }
    
    setRentLoading(true);
    setRentAnalysis(null);
    toast.info("📊 Analyzing rental market...");
    
    try {
      const res = await api.post("/ai/real-estate/rent-value-analysis", {
        address: rentAddress,
        bedrooms: rentBedrooms ? parseInt(rentBedrooms) : null,
        bathrooms: rentBathrooms ? parseFloat(rentBathrooms) : null,
        square_feet: rentSqft ? parseInt(rentSqft) : null
      });
      setRentAnalysis(res.data);
      toast.success("Analysis complete!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Analysis failed");
    } finally {
      setRentLoading(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>
            Real Estate Research
          </h1>
          <p className="text-zinc-500 mt-1">AI-powered property analysis for house flipping and rental valuation</p>
        </div>

        <Tabs defaultValue="flip" className="w-full">
          <TabsList className="bg-zinc-900 border border-white/10 rounded-none">
            <TabsTrigger value="flip" className="data-[state=active]:bg-orange-500/20 data-[state=active]:text-orange-400 rounded-none">
              <TrendingUp className="w-4 h-4 mr-2" />
              House Flip Analysis
            </TabsTrigger>
            <TabsTrigger value="rent" className="data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-400 rounded-none">
              <DollarSign className="w-4 h-4 mr-2" />
              Rent Value Analysis
            </TabsTrigger>
          </TabsList>

          {/* House Flip Tab */}
          <TabsContent value="flip" className="mt-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Input Form */}
              <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
                <CardHeader>
                  <CardTitle className="text-lg text-white flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                    <Home className="w-5 h-5 text-orange-400" />
                    Deep Dive House Flip Analyzer
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-zinc-400">
                    Enter a property address to get <span className="text-orange-400 font-medium">deep web research</span> on the property, detailed itemized renovation costs, and comprehensive ROI analysis.
                  </p>
                  
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Property Address *</Label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                      <Input
                        value={flipAddress}
                        onChange={(e) => setFlipAddress(e.target.value)}
                        placeholder="123 Main St, City, State ZIP"
                        className="bg-black/50 border-white/10 rounded-sm pl-10"
                      />
                    </div>
                    <p className="text-xs text-zinc-500">We'll search Zillow, Realtor.com & Trulia for active listings</p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Purchase Price (Optional)</Label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                      <Input
                        type="number"
                        value={flipPrice}
                        onChange={(e) => setFlipPrice(e.target.value)}
                        placeholder="250000"
                        className="bg-black/50 border-white/10 rounded-sm pl-10"
                      />
                    </div>
                    <p className="text-xs text-zinc-500">Leave blank for AI to estimate based on area</p>
                  </div>
                  
                  <Button
                    onClick={analyzeFlip}
                    disabled={flipLoading}
                    className="w-full bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white rounded-sm"
                  >
                    {flipLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <Search className="w-4 h-4 mr-2" />
                        Analyze Flip Potential
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>

              {/* Results */}
              <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
                <CardHeader>
                  <CardTitle className="text-lg text-white flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                    <FileText className="w-5 h-5 text-orange-400" />
                    Deep Dive Analysis Report
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {flipLoading && (
                    <div className="flex flex-col items-center justify-center py-12">
                      <Loader2 className="w-12 h-12 text-orange-400 animate-spin mb-4" />
                      <p className="text-zinc-400">Searching property listings & analyzing...</p>
                      <p className="text-xs text-zinc-500 mt-1">Searching Zillow, Realtor.com, Trulia...</p>
                      <p className="text-xs text-zinc-500">This may take 30-60 seconds for deep analysis</p>
                    </div>
                  )}
                  
                  {!flipLoading && !flipAnalysis && (
                    <div className="flex flex-col items-center justify-center py-12 text-zinc-500">
                      <Calculator className="w-12 h-12 mb-4 opacity-50" />
                      <p>Enter an address to get deep dive analysis</p>
                      <p className="text-xs mt-2">Includes web research, itemized repair costs & ROI</p>
                    </div>
                  )}
                  
                  {flipAnalysis && (
                    <div className="space-y-4 max-h-[700px] overflow-y-auto">
                      {/* Property Header */}
                      <div className="p-3 bg-orange-500/10 border border-orange-500/30 rounded-sm">
                        <p className="text-sm text-orange-400 font-medium mb-1">📍 {flipAnalysis.address}</p>
                        <p className="text-xs text-zinc-500">Analyzed: {new Date(flipAnalysis.timestamp).toLocaleString()}</p>
                      </div>
                      
                      {/* Web Research Status */}
                      {flipAnalysis.web_research && (
                        <div className={`p-3 rounded-sm border ${
                          flipAnalysis.web_research.is_active_listing 
                            ? 'bg-green-500/10 border-green-500/30' 
                            : flipAnalysis.web_research.listing_status === 'sold'
                            ? 'bg-red-500/10 border-red-500/30'
                            : flipAnalysis.web_research.listing_status === 'pending'
                            ? 'bg-yellow-500/10 border-yellow-500/30'
                            : flipAnalysis.web_research.listing_status === 'off_market'
                            ? 'bg-orange-500/10 border-orange-500/30'
                            : 'bg-zinc-500/10 border-zinc-500/30'
                        }`}>
                          <p className={`text-sm font-medium ${
                            flipAnalysis.web_research.is_active_listing 
                              ? 'text-green-400' 
                              : flipAnalysis.web_research.listing_status === 'sold'
                              ? 'text-red-400'
                              : flipAnalysis.web_research.listing_status === 'pending'
                              ? 'text-yellow-400'
                              : flipAnalysis.web_research.listing_status === 'off_market'
                              ? 'text-orange-400'
                              : 'text-zinc-400'
                          }`}>
                            {flipAnalysis.web_research.is_active_listing 
                              ? '✅ ACTIVE MLS LISTING - Currently For Sale' 
                              : flipAnalysis.web_research.listing_status === 'sold'
                              ? '❌ SOLD - Property Already Sold'
                              : flipAnalysis.web_research.listing_status === 'pending'
                              ? '⏳ PENDING - Under Contract'
                              : flipAnalysis.web_research.listing_status === 'off_market'
                              ? '🚫 OFF-MARKET - Not Listed for Sale'
                              : '❓ No MLS Listing Found'}
                          </p>
                          {flipAnalysis.web_research.sources_searched?.length > 0 && (
                            <p className="text-xs text-zinc-500 mt-1">
                              Sources: {flipAnalysis.web_research.sources_searched.join(', ')}
                            </p>
                          )}
                          {flipAnalysis.web_research.property_details && Object.keys(flipAnalysis.web_research.property_details).length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-2">
                              {Object.entries(flipAnalysis.web_research.property_details).map(([key, value]) => (
                                <span key={key} className="text-xs bg-black/30 px-2 py-1 rounded text-zinc-300">
                                  {key}: {value}
                                </span>
                              ))}
                            </div>
                          )}
                          {!flipAnalysis.web_research.is_active_listing && flipAnalysis.web_research.listing_status !== 'unknown' && (
                            <p className="text-xs text-orange-300 mt-2 font-medium">
                              ⚠️ This property is NOT available for purchase through MLS
                            </p>
                          )}
                        </div>
                      )}
                      
                      {/* Analysis Content */}
                      <div className="prose prose-invert prose-sm max-w-none bg-black/30 p-4 rounded 
                        [&>h1]:text-orange-400 [&>h1]:text-xl [&>h1]:font-bold [&>h1]:mt-6 [&>h1]:mb-3
                        [&>h2]:text-orange-300 [&>h2]:text-lg [&>h2]:font-semibold [&>h2]:mt-5 [&>h2]:mb-2 [&>h2]:border-b [&>h2]:border-orange-500/20 [&>h2]:pb-1
                        [&>h3]:text-zinc-200 [&>h3]:text-base [&>h3]:font-medium [&>h3]:mt-4 [&>h3]:mb-2
                        [&>h4]:text-zinc-300 [&>h4]:font-medium
                        [&>p]:text-zinc-300 [&>p]:my-2
                        [&>ul]:text-zinc-300 [&>ul]:my-2
                        [&>ol]:text-zinc-300 [&>ol]:my-2
                        [&>li]:text-zinc-300 [&>li]:my-1
                        [&>strong]:text-white
                        [&>table]:w-full [&>table]:my-4 [&>table]:border-collapse
                        [&>table>thead>tr>th]:bg-orange-500/20 [&>table>thead>tr>th]:text-orange-300 [&>table>thead>tr>th]:p-2 [&>table>thead>tr>th]:text-left [&>table>thead>tr>th]:border [&>table>thead>tr>th]:border-orange-500/30
                        [&>table>tbody>tr>td]:p-2 [&>table>tbody>tr>td]:border [&>table>tbody>tr>td]:border-zinc-700 [&>table>tbody>tr>td]:text-zinc-300
                        [&>table>tbody>tr:nth-child(odd)]:bg-black/20
                        [&>blockquote]:border-l-2 [&>blockquote]:border-orange-500 [&>blockquote]:pl-4 [&>blockquote]:italic [&>blockquote]:text-zinc-400
                        [&>hr]:border-zinc-700 [&>hr]:my-4">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{flipAnalysis.analysis}</ReactMarkdown>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Rent Value Tab */}
          <TabsContent value="rent" className="mt-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Input Form */}
              <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
                <CardHeader>
                  <CardTitle className="text-lg text-white flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                    <Building className="w-5 h-5 text-blue-400" />
                    Rent Value Analysis
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-zinc-400">
                    Get AI-powered rental market analysis including comparable properties and optimal rent pricing.
                  </p>
                  
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Property Address *</Label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                      <Input
                        value={rentAddress}
                        onChange={(e) => setRentAddress(e.target.value)}
                        placeholder="123 Main St, City, State ZIP"
                        className="bg-black/50 border-white/10 rounded-sm pl-10"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-2">
                      <Label className="text-zinc-300">Bedrooms</Label>
                      <Input
                        type="number"
                        value={rentBedrooms}
                        onChange={(e) => setRentBedrooms(e.target.value)}
                        placeholder="3"
                        className="bg-black/50 border-white/10 rounded-sm"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-300">Bathrooms</Label>
                      <Input
                        type="number"
                        step="0.5"
                        value={rentBathrooms}
                        onChange={(e) => setRentBathrooms(e.target.value)}
                        placeholder="2"
                        className="bg-black/50 border-white/10 rounded-sm"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-300">Sq Ft</Label>
                      <Input
                        type="number"
                        value={rentSqft}
                        onChange={(e) => setRentSqft(e.target.value)}
                        placeholder="1500"
                        className="bg-black/50 border-white/10 rounded-sm"
                      />
                    </div>
                  </div>
                  
                  <Button
                    onClick={analyzeRent}
                    disabled={rentLoading}
                    className="w-full bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 text-white rounded-sm"
                  >
                    {rentLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <Search className="w-4 h-4 mr-2" />
                        Analyze Rental Value
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>

              {/* Results */}
              <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
                <CardHeader>
                  <CardTitle className="text-lg text-white flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                    <FileText className="w-5 h-5 text-blue-400" />
                    Analysis Results
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {rentLoading && (
                    <div className="flex flex-col items-center justify-center py-12">
                      <Loader2 className="w-12 h-12 text-blue-400 animate-spin mb-4" />
                      <p className="text-zinc-400">AI is analyzing the rental market...</p>
                      <p className="text-xs text-zinc-500 mt-1">This may take 15-30 seconds</p>
                    </div>
                  )}
                  
                  {!rentLoading && !rentAnalysis && (
                    <div className="flex flex-col items-center justify-center py-12 text-zinc-500">
                      <Calculator className="w-12 h-12 mb-4 opacity-50" />
                      <p>Enter an address to get rental analysis</p>
                    </div>
                  )}
                  
                  {rentAnalysis && (
                    <div className="space-y-4 max-h-[500px] overflow-y-auto">
                      <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-sm">
                        <p className="text-xs text-blue-400 mb-1">Property: {rentAnalysis.address}</p>
                        <p className="text-xs text-zinc-500">Analyzed: {new Date(rentAnalysis.timestamp).toLocaleString()}</p>
                      </div>
                      <div className="prose prose-invert prose-sm max-w-none bg-black/30 p-4 rounded [&>h1]:text-blue-400 [&>h2]:text-blue-300 [&>h3]:text-zinc-200 [&>h4]:text-zinc-300 [&>p]:text-zinc-300 [&>ul]:text-zinc-300 [&>ol]:text-zinc-300 [&>li]:text-zinc-300 [&>strong]:text-white [&>table]:w-full [&>table]:my-4 [&>table]:border-collapse [&>table>thead>tr>th]:bg-blue-500/20 [&>table>thead>tr>th]:text-blue-300 [&>table>thead>tr>th]:p-2 [&>table>thead>tr>th]:text-left [&>table>thead>tr>th]:border [&>table>thead>tr>th]:border-blue-500/30 [&>table>tbody>tr>td]:p-2 [&>table>tbody>tr>td]:border [&>table>tbody>tr>td]:border-zinc-700 [&>table>tbody>tr>td]:text-zinc-300">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{rentAnalysis.analysis}</ReactMarkdown>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
